import type { Track, TrackWithSave } from '../interfaces/track';
import type { TrackMatchSummary } from '../services/recordings';

const SPOTIFY_TRACK_ID_REGEX = /^[0-9A-Za-z]{22}$/;

type TrackLike = (Track | TrackWithSave) & Record<string, any>;

const isObject = (value: unknown): value is Record<string, any> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const cloneBucket = (value: unknown): Record<string, any> => {
  if (isObject(value)) {
    return { ...value };
  }
  return {};
};

const assignIfMissing = (bucket: Record<string, any>, key: string, value: unknown) => {
  if (value === undefined || value === null) {
    return;
  }
  if (bucket[key] !== undefined && bucket[key] !== null) {
    return;
  }
  bucket[key] = value;
};

const ensureNestedBucket = (parent: Record<string, any>, key: string): Record<string, any> => {
  const current = parent[key];
  const next = cloneBucket(current);
  parent[key] = next;
  return next;
};

const tryExtractSpotifyId = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  if (SPOTIFY_TRACK_ID_REGEX.test(trimmed)) {
    return trimmed;
  }
  const uriMatch = trimmed.match(/^spotify:track:([0-9A-Za-z]{22})$/i);
  if (uriMatch) {
    return uriMatch[1];
  }
  return undefined;
};

export const extractSpotifyTrackId = (track?: Partial<Track> | null): string | undefined => {
  if (!track) return undefined;

  const candidates: Array<unknown> = [
    (track as any).spotifyTrackId,
    (track as any).spotify_track_id,
    (track as any).metadata?.spotifyTrackId,
    (track as any).metadata?.spotify_track_id,
    (track as any).classical?.spotifyTrackId,
    (track as any).catalog?.spotifyTrackId,
    (track as any).catalog?.spotify_track_id,
    (track as any).catalogMetadata?.spotifyTrackId,
    (track as any).catalogMetadata?.spotify_track_id,
    track.id,
    track.uri,
  ];

  for (const candidate of candidates) {
    const resolved = tryExtractSpotifyId(candidate);
    if (resolved) {
      return resolved;
    }
  }

  return undefined;
};

export const applyTrackMatchToTrack = <T extends TrackLike>(
  track: T,
  match: TrackMatchSummary
): T => {
  const enriched: Record<string, any> = { ...track };

  assignIfMissing(enriched, 'workId', match.workId);
  assignIfMissing(enriched, 'workName', match.workName);
  assignIfMissing(enriched, 'recordingId', match.recordingId);

  const metadata = ensureNestedBucket(enriched, 'metadata');
  assignIfMissing(metadata, 'workId', match.workId);
  assignIfMissing(metadata, 'work_id', match.workId);
  assignIfMissing(metadata, 'recordingId', match.recordingId);
  assignIfMissing(metadata, 'recording_id', match.recordingId);

  const classical = ensureNestedBucket(enriched, 'classical');
  assignIfMissing(classical, 'workId', match.workId);
  assignIfMissing(classical, 'work_id', match.workId);
  assignIfMissing(classical, 'composerId', match.composerId);
  assignIfMissing(classical, 'composer_id', match.composerId);
  assignIfMissing(classical, 'composerName', match.composerName);
  assignIfMissing(classical, 'composer_name', match.composerName);

  const catalog = ensureNestedBucket(enriched, 'catalog');
  assignIfMissing(catalog, 'workId', match.workId);
  assignIfMissing(catalog, 'work_id', match.workId);
  assignIfMissing(catalog, 'recordingId', match.recordingId);
  assignIfMissing(catalog, 'recording_id', match.recordingId);
  assignIfMissing(catalog, 'spotifyAlbumId', match.spotifyAlbumId);
  assignIfMissing(catalog, 'spotify_album_id', match.spotifyAlbumId);
  if (typeof match.confidence === 'number' && catalog.confidence === undefined) {
    catalog.confidence = match.confidence;
  }

  const catalogWork = ensureNestedBucket(catalog, 'work');
  assignIfMissing(catalogWork, 'id', match.workId);
  assignIfMissing(catalogWork, 'name', match.workName);
  assignIfMissing(catalogWork, 'composerId', match.composerId);
  assignIfMissing(catalogWork, 'composerName', match.composerName);

  const catalogRecording = ensureNestedBucket(catalog, 'recording');
  assignIfMissing(catalogRecording, 'id', match.recordingId);
  assignIfMissing(catalogRecording, 'name', match.recordingName);
  assignIfMissing(catalogRecording, 'albumName', match.albumName);
  assignIfMissing(catalogRecording, 'albumImageUrl', match.albumImageUrl);
  assignIfMissing(catalogRecording, 'performerName', match.performerName);

  const catalogMetadata = ensureNestedBucket(enriched, 'catalogMetadata');
  assignIfMissing(catalogMetadata, 'workId', match.workId);
  assignIfMissing(catalogMetadata, 'work_id', match.workId);
  assignIfMissing(catalogMetadata, 'recordingId', match.recordingId);
  assignIfMissing(catalogMetadata, 'recording_id', match.recordingId);

  if (match.workName) {
    assignIfMissing(enriched, 'workName', match.workName);
  }

  return enriched as T;
};

export const hydrateTracksWithMatches = <T extends TrackLike>(
  tracks: T[],
  matches: Record<string, TrackMatchSummary>
): T[] => {
  if (!tracks.length || !matches || Object.keys(matches).length === 0) {
    return tracks;
  }

  return tracks.map((track) => {
    const id = extractSpotifyTrackId(track);
    if (!id) {
      return track;
    }
    const match = matches[id];
    if (!match) {
      return track;
    }
    return applyTrackMatchToTrack(track, match);
  });
};
